import React from "react";
import PropTypes from "prop-types";
import Panel from "wonderbly-components/lib/Panel";
import Button from "wonderbly-components/lib/Button";
import Icon from "wonderbly-components/lib/Icon";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import useGlobalContent from "../../../hooks/use-global-content";

import "./CartOrderSummary.scss";

const getShippingRules = (props) => {
  const hasShippingInfo =
    props.label?.estimatedShipping && props.value?.estimatedShipping;
  const hasShippingDiscount = !!(
    props.value?.discountAmount?.shipping_discount?.amount !== "0.0"
  );
  const hasShippingDiscountInfo =
    props.label?.shippingDiscount && props.showShippingPrice;
  const shippingDiscountDiff = props.value?.estimatedShipping
    ? parseFloat(props.value?.estimatedShipping?.amount) +
      parseFloat(props.value?.discountAmount?.shipping_discount?.amount)
    : null;

  return {
    hasShippingInfo,
    hasShippingDiscount,
    hasShippingDiscountInfo,
    shippingDiscountDiff,
  };
};

const ShippingPrice = (props) => {
  // in Checkout, we show the estimated shipping price ONLY if shipping_estimated is true(coming from Eagle)

  // in Cart the shipping is estimaed by default - we want the option to show or hide the estimated shipping price by adding
  // or removing the estimatedShipping label in Prismic
  const isShippingEstimated = get(
    props,
    "summary.totals_v2.shipping_estimated",
    false
  );
  const showShippingPrice = props.showShippingPrice && !isShippingEstimated;

  const { freeShipping } = useGlobalContent();
  const { hasShippingInfo, hasShippingDiscount, shippingDiscountDiff } =
    getShippingRules(props);

  if (showShippingPrice && hasShippingInfo) {
    return (
      <div className="cart-order-summary-row">
        <p className="cart-order-summary-label">
          {props.label.estimatedShipping}
        </p>
        <p className="cart-order-summary-price">
          {hasShippingDiscount && shippingDiscountDiff === 0 ? (
            <>
              <span className="cart-order-summary-price-crossed">
                {props.value?.estimatedShipping?.formatted}
              </span>
              {freeShipping.shippingMethodsLabel ||
                props.summary.orderDetails.selectedShipment
                  ?.discounted_shipping_rate?.formatted}
            </>
          ) : (
            props.value?.estimatedShipping?.formatted
          )}
        </p>
      </div>
    );
  }
  return null;
};

const ShippingDiscount = (props) => {
  const { hasShippingDiscount, hasShippingDiscountInfo, shippingDiscountDiff } =
    getShippingRules(props);

  if (
    props.showShippingAndTaxes &&
    hasShippingDiscount &&
    hasShippingDiscountInfo &&
    shippingDiscountDiff !== 0
  ) {
    return (
      <div className="cart-order-summary-row">
        <p className="cart-order-summary-label">
          {props.label.shippingDiscount}
        </p>
        <p className="cart-order-summary-price">
          {props.value?.discountAmount?.shipping_discount?.formatted}
        </p>
      </div>
    );
  }
  return null;
};

const CartOrderSummary = (props) => {
  const showShippingAndTaxes = props.showShippingAndTaxes ?? true;
  const totalKey = showShippingAndTaxes ? "discountedTotal" : "itemTotal";
  const showDiscounts =
    props.value?.discountCode || props.value?.discountAmount;

  return (
    <Panel className="cart-order-summary">
      <div className="cart-order-summary-list">
        {props.value.itemTotal && showShippingAndTaxes && (
          <div className="cart-order-summary-row">
            <p className="cart-order-summary-label">{props.label.itemTotal}</p>
            <p className="cart-order-summary-price">
              {props.value.itemTotal.formatted}
            </p>
          </div>
        )}
        {props.showSalesTax &&
          props.label.salesTaxLabel &&
          !isEmpty(props.value.salesTax) && (
            <div className="cart-order-summary-row">
              <p className="cart-order-summary-label">
                {props.label.salesTaxLabel}
              </p>
              <p className="cart-order-summary--sales-tax">
                {props.value.salesTax.formatted}
              </p>
            </div>
          )}
        {props.showIncludedTax &&
          props.label.includedTaxLabel &&
          !isEmpty(props.value.includedTax) && (
            <div className="cart-order-summary-row">
              <p className="cart-order-summary-label">
                {props.label.includedTaxLabel}
              </p>
              <p className="cart-order-summary--included-tax">
                {props.value.includedTax.formatted}
              </p>
            </div>
          )}
        <ShippingPrice {...props} />
        <ShippingDiscount {...props} />
        {props.label.seeExactCostsLabel && (
          <div className="text-micro see-exact-costs">
            {props.label.seeExactCostsLabel}
          </div>
        )}
        {props.label.storeCredit && !isEmpty(props.value.storeCredit) && (
          <div className="cart-order-summary-row cart-order-summary-row__store-credit">
            <p className="cart-order-summary-label">
              {props.label.storeCredit}
            </p>
            <p className="cart-order-summary-price">
              {props.value.storeCredit.formatted}
            </p>
          </div>
        )}

        {showDiscounts && (
          <>
            {props.value.discountAmount && (
              <div className="cart-order-summary-row cart-order-summary-row--discount">
                <p className="cart-order-summary-label">
                  {props.label.discountAmount}
                </p>

                {props.value.discountAmount.discount_ex_shipping_discount
                  ?.amount !== "0.0" && (
                  <p className="cart-order-summary-price">
                    {
                      props.value.discountAmount.discount_ex_shipping_discount
                        ?.formatted
                    }
                  </p>
                )}
              </div>
            )}

            {props.value.discountCode && (
              <div className="cart-order-summary-row cart-order-summary-row--discount-code">
                <p
                  className="cart-order-summary-label"
                  data-testid="promo-code-label"
                >
                  {props.value.discountCode}
                </p>
                <Button
                  unstyled
                  onClick={props.onRemoveCode}
                  data-testid="promo-code-remove"
                >
                  {props.removeCta}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {props.value[totalKey] && (
        <div className="cart-order-summary-row cart-order-summary-row--large cart-order-summary-row--total">
          <p className="cart-order-summary-label">{props.label[totalKey]}</p>

          <p className="cart-order-summary-price color-green">
            {props.value[totalKey].formatted}
          </p>
        </div>
      )}

      {!showShippingAndTaxes && (
        <>
          <hr />
          <div className="cart-order-summary-row cart-order-summary-row--bottom-hint">
            <Icon className="icon" icon="info" />
            <span>{get(props, "bottomText.0.text", "placeholder text")}</span>
          </div>
        </>
      )}
    </Panel>
  );
};

const propTypes = {
  label: PropTypes.shape({
    itemTotal: PropTypes.string.isRequired,
    discountedTotal: PropTypes.string.isRequired,
    discountCode: PropTypes.string,
    discountAmount: PropTypes.string,
    estimatedShipping: PropTypes.string,
    storeCredit: PropTypes.string,
    seeExactCostsLabel: PropTypes.string,
    salesTaxLabel: PropTypes.string,
    includedTaxLabel: PropTypes.string,
    shippingDiscount: PropTypes.string,
  }),
  value: PropTypes.shape({
    discountCode: PropTypes.string,
    itemTotal: PropTypes.shape({
      formatted: PropTypes.string.isRequired,
    }),
    discountedTotal: PropTypes.shape({
      formatted: PropTypes.string.isRequired,
    }),
    discountAmount: PropTypes.shape({
      formatted: PropTypes.string,
      amount: PropTypes.string,
      shipping_discount: PropTypes.shape({
        formatted: PropTypes.string,
        amount: PropTypes.string,
      }),
      discount_ex_shipping_discount: PropTypes.shape({
        formatted: PropTypes.string,
        amount: PropTypes.string,
      }),
    }),
    estimatedShipping: PropTypes.shape({
      formatted: PropTypes.string,
      amount: PropTypes.string,
    }),
    storeCredit: PropTypes.shape({
      formatted: PropTypes.string,
    }),
    salesTax: PropTypes.shape({
      formatted: PropTypes.string,
    }),
    includedTax: PropTypes.shape({
      formatted: PropTypes.string,
    }),
  }),
  summary: PropTypes.shape({
    orderDetails: PropTypes.any,
  }),
  onRemoveCode: PropTypes.func.isRequired,
  removeCta: PropTypes.string,
  showSalesTax: PropTypes.bool,
  showIncludedTax: PropTypes.bool,
  showShippingPrice: PropTypes.bool,
  showShippingAndTaxes: PropTypes.bool,
  showDiscounts: PropTypes.bool,
};

CartOrderSummary.defaultProps = {
  showShippingAndTaxes: true,
};

CartOrderSummary.propTypes = propTypes;
ShippingPrice.propTypes = propTypes;
ShippingDiscount.propTypes = propTypes;

export default CartOrderSummary;
